import React, { useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { SOURCE_OPTIONS } from "../constants/dashboard.constants";
import {
  registerLeadService,
  RegisterLeadResponse,
} from "../services/registerLeadService";
import { isValidEmail } from "../utils/validation";

interface RegisterLeadForm {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  visaType: string;
  countryCode: string;
  sourceChannel: string;
  enquirySubject: string;
  consultationDate: string;
  notes: string;
}

const DEFAULT_VISA_TYPE = "Visitor visa";
const DEFAULT_COUNTRY_CODE = "NZ";
const DEFAULT_SOURCE_CHANNEL = "web";
const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

const isUuid = (value: string): boolean => UUID_PATTERN.test(value.trim());

const extractApiError = (error: unknown, fallback: string): string => {
  if (
    typeof error === "object" &&
    error !== null &&
    "response" in error &&
    typeof (error as { response?: { data?: { detail?: string } } }).response?.data?.detail ===
      "string"
  ) {
    return (error as { response?: { data?: { detail?: string } } }).response?.data?.detail ?? fallback;
  }

  if (error instanceof Error && error.message) {
    return error.message;
  }

  return fallback;
};

const RegisterLeadPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const prefilledFirmId = useMemo(
    () =>
      searchParams.get("firm_id")?.trim() ||
      searchParams.get("firmId")?.trim() ||
      "",
    [searchParams]
  );
  const prefilledAssistantId = useMemo(
    () =>
      searchParams.get("assistant_id")?.trim() ||
      searchParams.get("assistantId")?.trim() ||
      "",
    [searchParams]
  );

  const [form, setForm] = useState<RegisterLeadForm>({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    visaType: DEFAULT_VISA_TYPE,
    countryCode: DEFAULT_COUNTRY_CODE,
    sourceChannel: DEFAULT_SOURCE_CHANNEL,
    enquirySubject: "",
    consultationDate: "",
    notes: "",
  });
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitSuccess, setSubmitSuccess] = useState<RegisterLeadResponse | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const normalizedFirmId = prefilledFirmId.trim();
  const hasFirmId = normalizedFirmId.length > 0;
  const hasValidFirmId = isUuid(normalizedFirmId);
  const leadEmailInvalid = form.email.trim().length > 0 && !isValidEmail(form.email);

  const isFormComplete = useMemo(() => {
    const hasCountryCode = form.countryCode.trim().length === 2;
    const hasValidEmail = isValidEmail(form.email);
    return (
      hasValidFirmId &&
      form.firstName.trim().length > 0 &&
      form.lastName.trim().length > 0 &&
      hasValidEmail &&
      form.visaType.trim().length > 0 &&
      hasCountryCode
    );
  }, [form, hasValidFirmId]);

  const isSubmitDisabled = submitting || !isFormComplete;

  const updateField = <K extends keyof RegisterLeadForm>(field: K, value: RegisterLeadForm[K]) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    if (submitError) setSubmitError(null);
    if (submitSuccess) setSubmitSuccess(null);
  };

  const handleCountryCodeChange = (value: string) => {
    const nextCountryCode = value.toUpperCase();
    setForm((prev) => ({ ...prev, countryCode: nextCountryCode }));
    if (submitError) setSubmitError(null);
    if (submitSuccess) setSubmitSuccess(null);
  };

  const handleVisaTypeChange = (value: string) => {
    setForm((prev) => ({ ...prev, visaType: value }));
    if (submitError) setSubmitError(null);
    if (submitSuccess) setSubmitSuccess(null);
  };

  const handleSubmit = async () => {
    if (!isFormComplete) {
      if (!hasFirmId) {
        setSubmitError("Missing firm_id in URL querystring.");
        return;
      }
      if (!hasValidFirmId) {
        setSubmitError("firm_id query param must be a valid UUID.");
        return;
      }
      if (!isValidEmail(form.email)) {
        setSubmitError("Please enter a valid email.");
        return;
      }

      setSubmitError("First name, last name, email, visa type, and a 2-letter country code are required.");
      return;
    }

    setSubmitting(true);
    setSubmitError(null);
    setSubmitSuccess(null);

    try {
      const createdLead = await registerLeadService.registerLead({
        firm_id: normalizedFirmId,
        first_name: form.firstName.trim(),
        last_name: form.lastName.trim(),
        email: form.email.trim(),
        phone: form.phone.trim() || undefined,
        visa_type: form.visaType.trim(),
        country_code: form.countryCode.trim().toUpperCase(),
        source_channel: form.sourceChannel.trim() || undefined,
        enquiry_subject: form.enquirySubject.trim() || undefined,
        consultation_date: form.consultationDate.trim() || undefined,
        notes: form.notes.trim() || undefined,
        assigned_assistant_id: prefilledAssistantId || undefined,
      });

      setSubmitSuccess(createdLead);
      setForm((prev) => ({
        ...prev,
        firstName: "",
        lastName: "",
        email: "",
        phone: "",
        visaType: DEFAULT_VISA_TYPE,
        countryCode: DEFAULT_COUNTRY_CODE,
        sourceChannel: DEFAULT_SOURCE_CHANNEL,
        enquirySubject: "",
        consultationDate: "",
        notes: "",
      }));
    } catch (error) {
      setSubmitError(extractApiError(error, "Could not register your interest right now."));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="login-page">
      <div className="login-shell">
        <aside className="login-brand card">
          <div className="logo" role="img" aria-label="CaseFlow" />
          <span className="badge badge-teal login-badge">
            <i className="ti ti-passport" /> Lead registration
          </span>
          <div className="login-brand-copy">
            <h1 className="login-title">Register Your Visa Interest</h1>
            <p className="login-subtitle">
              Share your details and our team will contact you to guide your visa journey.
            </p>
          </div>

          <div className="login-points">
            <div className="login-point">
              <i className="ti ti-file-pencil" />
              <div>
                <div className="login-point-title">Simple intake form</div>
                <div className="login-point-sub">
                  Add your basic details and preferred visa pathway.
                </div>
              </div>
            </div>
            <div className="login-point">
              <i className="ti ti-calendar-time" />
              <div>
                <div className="login-point-title">Consultation follow-up</div>
                <div className="login-point-sub">
                  You can optionally share a preferred consultation date.
                </div>
              </div>
            </div>
            <div className="login-point">
              <i className="ti ti-users" />
              <div>
                <div className="login-point-title">Team reaches out</div>
                <div className="login-point-sub">
                  Your request is captured directly in the lead pipeline.
                </div>
              </div>
            </div>
          </div>
        </aside>

        <section className="login-form-card card">
          <div className="card-hd">
            <i className="ti ti-user-plus" /> Register lead interest
          </div>
          <p className="login-form-copy">
            Fill in your details. Required fields are marked with
            {" "}
            <strong>*</strong>.
          </p>

          {!hasFirmId || !hasValidFirmId ? (
            <section className="notice notice-red">
              <i className="ti ti-alert-triangle" />
              <span>
                {!hasFirmId
                  ? "Missing firm_id query param. Use /register-lead?firm_id=<firm-uuid>."
                  : "Invalid firm_id query param. It must be a valid UUID."}
              </span>
            </section>
          ) : null}

          {submitError ? (
            <section className="notice notice-red">
              <i className="ti ti-alert-triangle" />
              <span>{submitError}</span>
            </section>
          ) : null}

          {submitSuccess ? (
            <section className="notice notice-green">
              <i className="ti ti-check" />
              <span>
                Thanks for registering your interest. Your lead number is
                {" "}
                <strong>{submitSuccess.lead_number}</strong>.
              </span>
            </section>
          ) : null}

          <form
            className="signup-form"
            onSubmit={(event) => {
              event.preventDefault();
              void handleSubmit();
            }}
          >
            <div className="field-group">
              <div className="field">
                <label htmlFor="register-lead-first-name">First name *</label>
                <input
                  id="register-lead-first-name"
                  placeholder="Aarav"
                  value={form.firstName}
                  onChange={(event) => updateField("firstName", event.target.value)}
                />
              </div>
              <div className="field">
                <label htmlFor="register-lead-last-name">Last name *</label>
                <input
                  id="register-lead-last-name"
                  placeholder="Sharma"
                  value={form.lastName}
                  onChange={(event) => updateField("lastName", event.target.value)}
                />
              </div>
            </div>

            <div className="field-group">
              <div className="field">
                <label htmlFor="register-lead-email">Email *</label>
                <input
                  id="register-lead-email"
                  type="email"
                  placeholder="you@example.com"
                  value={form.email}
                  className={leadEmailInvalid ? "input-invalid" : undefined}
                  aria-invalid={leadEmailInvalid}
                  onChange={(event) => updateField("email", event.target.value)}
                />
                {leadEmailInvalid ? (
                  <div className="field-inline-error">Enter a valid email address.</div>
                ) : null}
              </div>
              <div className="field">
                <label htmlFor="register-lead-phone">Phone</label>
                <input
                  id="register-lead-phone"
                  placeholder="+64 21 555 0182"
                  value={form.phone}
                  onChange={(event) => updateField("phone", event.target.value)}
                />
              </div>
            </div>

            <div className="field-group">
              <div className="field">
                <label htmlFor="register-lead-visa-type">Visa type *</label>
                <input
                  id="register-lead-visa-type"
                  placeholder="Visitor visa"
                  value={form.visaType}
                  onChange={(event) => handleVisaTypeChange(event.target.value)}
                />
              </div>
              <div className="field">
                <label htmlFor="register-lead-country-code">Country code *</label>
                <input
                  id="register-lead-country-code"
                  maxLength={2}
                  placeholder="NZ"
                  value={form.countryCode}
                  onChange={(event) => handleCountryCodeChange(event.target.value)}
                />
              </div>
            </div>

            <div className="field-group">
              <div className="field">
                <label htmlFor="register-lead-source-channel">Source channel</label>
                <select
                  id="register-lead-source-channel"
                  value={form.sourceChannel}
                  onChange={(event) => updateField("sourceChannel", event.target.value)}
                >
                  {SOURCE_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="field">
                <label htmlFor="register-lead-consultation-date">Preferred consultation date</label>
                <input
                  id="register-lead-consultation-date"
                  type="date"
                  value={form.consultationDate}
                  onChange={(event) => updateField("consultationDate", event.target.value)}
                />
              </div>
            </div>

            <div className="field">
              <label htmlFor="register-lead-enquiry-subject">Enquiry subject</label>
              <input
                id="register-lead-enquiry-subject"
                placeholder="Family visit in July"
                value={form.enquirySubject}
                onChange={(event) => updateField("enquirySubject", event.target.value)}
              />
            </div>

            <div className="field">
              <label htmlFor="register-lead-notes">Notes</label>
              <textarea
                id="register-lead-notes"
                rows={3}
                placeholder="Any additional details"
                value={form.notes}
                onChange={(event) => updateField("notes", event.target.value)}
              />
            </div>

            <div className="assistant-modal-actions">
              <button
                className="btn btn-sm"
                type="button"
                disabled={submitting}
                onClick={() => navigate("/login")}
              >
                Back to login
              </button>
              <button className="btn btn-primary btn-sm" type="submit" disabled={isSubmitDisabled}>
                <i className="ti ti-send" />
                {submitting ? "Submitting..." : "Register interest"}
              </button>
            </div>
          </form>
        </section>
      </div>
    </div>
  );
};

export default RegisterLeadPage;
